import { ReactNode } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TranscriptionExtended } from 'flow/types/CallRecord';
import { CallRecordKey, TicketKey } from 'flow/types/EntityKeys';
import {
    selectExtendedTranscriptionKey,
    selectExtendedTranscriptionIsPersonal,
} from 'flow/selectors/call-records-selectors';
import CardTranscriptionPending from './CardTranscriptionPending';
import CardTranscriptionFetching from './CardTranscriptionFetching';
import CardTranscriptionSummary from './CardTranscriptionSummary';
import CardTranscriptionError from './CardTranscriptionError';
import AnimatedHeight from './AnimatedHeight';

interface Props {
    extendedTranscription?: TranscriptionExtended;
    transcriptionIsPending?: boolean;
    callRecordKey: CallRecordKey;
    legacySummary?: string;
    isGeneratingLegacyTranscription?: boolean;
    awaitingTranscriptionConfirmation?: boolean;
    animateSummary?: boolean;
    hidePersonalContent?: boolean;
    displayTicketActions?: boolean;
    ticketKey?: TicketKey;
}

const SectionTranscriptionResults = ({
    extendedTranscription,
    transcriptionIsPending,
    callRecordKey,
    legacySummary,
    isGeneratingLegacyTranscription,
    awaitingTranscriptionConfirmation,
    animateSummary,
    hidePersonalContent,
    displayTicketActions,
    ticketKey,
}: Props) => {
    const extendedTranscriptionKey = useAppSelector((state) =>
        selectExtendedTranscriptionKey(state, callRecordKey),
    );
    const transcriptionIsPersonal = useAppSelector((state) =>
        selectExtendedTranscriptionIsPersonal(state, callRecordKey),
    );

    if (hidePersonalContent && transcriptionIsPersonal) return null;

    // Extended transcription exists on the server but the DTO hasn't been fetched yet.
    // Show a neutral skeleton rather than the "Transcribing call..." generating state.
    const isAwaitingExtendedTranscription =
        !!extendedTranscriptionKey && !extendedTranscription;

    const summary = extendedTranscription?.summary || legacySummary;
    const resultCode = extendedTranscription?.resultCode;

    let card: ReactNode = null;
    if (
        transcriptionIsPending ||
        isGeneratingLegacyTranscription ||
        awaitingTranscriptionConfirmation
    ) {
        card = <CardTranscriptionPending />;
    } else if (isAwaitingExtendedTranscription) {
        card = <CardTranscriptionFetching />;
    } else if (summary || (extendedTranscription && resultCode === 'SUCCESS')) {
        card = (
            <CardTranscriptionSummary
                summary={summary}
                actions={extendedTranscription?.actions}
                animate={animateSummary}
                displayTicketActions={displayTicketActions}
                ticketKey={ticketKey}
            />
        );
    } else if (resultCode && resultCode !== 'SUCCESS') {
        card = (
            <CardTranscriptionError
                resultCode={resultCode}
                callRecordKey={callRecordKey}
            />
        );
    }

    if (!card) return null;

    // Slower height transition when the summary is being animated in after generation,
    // snappier when we've just fetched a pre-existing transcription.
    return (
        <AnimatedHeight durationMs={animateSummary ? 150 : 50}>
            {card}
        </AnimatedHeight>
    );
};

export default SectionTranscriptionResults;
